import type { CheckpointInput } from '@hitz/sdk'
import { FAMILY } from './release.js'
import { MAX_STROKES, roundPar, totalStrokes } from '../core/rules'
import type { Session } from '../core/engine'

export type RoundStatus = 'playing' | 'won' | 'lost'

/**
 * Pure serialization of the round into a checkpoint (docs/EVENTS.md). `boardState` and `randomState` are JSON strings.
 * No player name, balance, stake, prize, token or host data may enter here. The terminal score of a lost round is 0
 * (family `finite`); the hole points stay visible in `scoreBreakdown`. `randomState.deterministic` is always `false`
 * (decision of the standard, 2026-11). The platform reads progress from `levelId` and `score` (contract `progress`).
 */
export function createCheckpoint(s: Session, status: RoundStatus, levelId: string, seed: number): CheckpointInput {
  const terminal = status !== 'playing'
  const score = status === 'lost' ? 0 : terminal ? s.score : s.state.score
  const board = {
    telemetrySchemaVersion: 1,
    family: FAMILY,
    status,
    levelId,
    holes: s.round.map((h) => `${h.def.id}#${h.cupVariant}`),
    hole: s.state.hole,
    strokes: s.state.strokes,
    cards: s.state.cards.map((c) => ({ id: c.id, par: c.par, strokes: c.strokes, bonus: c.bonus, points: c.points })),
    outcomeReason: status === 'lost' ? 'over_par' : null,
    scoreBreakdown: terminal ? { earned: s.state.score, counted: score, strokes: totalStrokes(s.state), par: roundPar(s.round) } : null
  }
  const left = s.round.length * MAX_STROKES - s.history.length
  return {
    boardState: JSON.stringify(board),
    randomState: JSON.stringify({ seed, deterministic: false }),
    movesRemaining: Math.max(0, left),
    score,
    goals: JSON.stringify({ family: FAMILY, par: roundPar(s.round), strokes: totalStrokes(s.state) }),
    restorable: false
  }
}
